// @ts-strict-ignore
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, Input } from "../../common/components/Input";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import ProfileInputStyle from "../../common/components/Input/ProfileInputStyle";
import type { RootState } from "../../common/state/store";
import type { ILanguage, IOrganization, IUser } from "../../model/model";
import {
  getCurrentOrganization,
  updateUser,
  getCurrentUser,
  setCurrentUserAndOrg,
  checkEmail,
  userOpenedTutorialReset,
  getUserOpenedTutorial,
} from "../../features/profile/state/profileSlice";
import { IsLoading } from "../../common/components/AppState/IsLoading";
import type { PayloadAction } from "@reduxjs/toolkit";
import ModalGallery from "../../features/graph/components/ModalGallery";
import ChangePasswordPopup from "../../common/components/Profile/ChangePasswordPopup";
import ChangeEmailPoppup from "../../common/components/Profile/ChangeMailConfirmPassword";
import Avatar3dPopup from "../../common/components/Profile/Avatar3dPopup";
import AvatarPreview3d from "../../common/components/Profile/AvatarPreview3d";
import { mediaSelector } from "../../features/gallery/state/gallerySlice";
import type { IMedia } from "../../model/unityObject";
import {
  closeStandardModal,
  getCurrentModalCurrentState,
} from "../../common/state/slice/modal/modalSlice";
import RoundImagePreview from "../../features/projects/components/RoundImagePreview";
import Spinner from "../../common/components/Loader/Spinner";
import { setAlert } from "../../features/alert/state/alertsSlice";
import { useCallbackPrompt } from "../../common/util/useCallbackPrompt";
import ModalConfirmBeforeNavigatingOut from "../../common/components/Modal/ModalConfirmBeforeNavigatingOut";
import { buildNextPreferences, getReleaseOptIn } from "./profileUtils";

const getAiProfile = (u?: IUser) => {
  try {
    const pref = (u as any)?.preferences;
    if (!pref) return { metier: "", targetAudience: "" };
    const obj = typeof pref === "string" ? JSON.parse(pref) : pref;
    return {
      metier: obj?.aiProfile?.metier || "",
      targetAudience: obj?.aiProfile?.targetAudience || "",
    };
  } catch {
    return { metier: "", targetAudience: "" };
  }
};
import { Tooltip } from "src/common/components/Tooltip/Tooltip";
export const PROFILE_COMPONENT_ID = "profileComponent";
export const AVATAR3D_COMPONENT_ID = "avatar3dComponent";
export const CHANGEPASSWORD_COMPONENT_ID = "changePasswordComponent";
export const CHECK_EMAIL_COMPONENT_ID = "CkeckEmailComponent";

type ProfileValues = {
  firstname: string;
  lastname: string;
  email: string;
  source_avatar: number;
  avatar: string;
  preferred_language: number;
  wantToReceiveEmailAboutReleases: boolean;
  ai_metier: string;
  ai_targetAudience: string;
};

const languageDropdownOptions = [
  {
    disabled: false,
    value: "1",
    optionText: "Français",
  },
  {
    disabled: false,
    value: "2",
    optionText: "English",
  },
];

const ProfileMain = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const [isConfirmPasswordOpen, setIsConfirmPasswordOpen] = useState(true);

  const { currentUser, organization, userOpenedTutorial } = useAppSelector((state: RootState) => {
    return {
      userOpenedTutorial: getUserOpenedTutorial(state),
      currentUser: getCurrentUser(state),
      organization: getCurrentOrganization(state),
    };
  });
  const medias = useAppSelector(mediaSelector);
  const stripQueryParams = (url?: string) => url?.split("?")[0];
  const matchingAvatar = medias.find(
    (m: IMedia) => stripQueryParams(m.s3_url) === stripQueryParams(currentUser?.avatar3d_url),
  );
  const avatarThumbnailUrl = matchingAvatar?.thumbnail_s3_url;
  // Use the signed s3_url from the gallery media if available, otherwise fall back to avatar3d_url directly
  const avatar3dSignedUrl = matchingAvatar?.s3_url || currentUser?.avatar3d_url;
  const isEnduser = currentUser?.role?.name === "Enduser";
  const buttonResetDisabled = !Object.values(userOpenedTutorial as any).some(
    (value) => value === true,
  );

  const setFocusOnInput = (inputId: "firstname" | "lastname") => {
    // Wait for the next render cycle to set the focus
    setTimeout(() => {
      setFocus(inputId);
    }, 0);
  };

  useEffect(() => {
    if (!currentUser || !organization) {
      void dispatch(setCurrentUserAndOrg({ componentId: PROFILE_COMPONENT_ID })).then((action) => {
        const temp = action as PayloadAction<{ currentUser: IUser; organization: IOrganization }>;

        const aiP = getAiProfile(temp.payload.currentUser);
        reset({
          firstname: temp.payload.currentUser?.firstname,
          lastname: temp.payload.currentUser?.lastname,
          email: temp.payload.currentUser?.email,
          source_avatar: Number(temp.payload.currentUser?.source_avatar?.id),
          wantToReceiveEmailAboutReleases: getReleaseOptIn(temp.payload.currentUser),
          ai_metier: aiP.metier,
          ai_targetAudience: aiP.targetAudience,
        });
      });
    }

    dispatch(closeStandardModal("modal-gallery"));
  }, []);

  const [disabledFirstName, setDisabledFirstName] = useState(true);
  const [disabledLastName, setDisabledLastName] = useState(true);
  const [disabledEmail, setDisabledEmail] = useState(true);
  const [disabledAiMetier, setDisabledAiMetier] = useState(true);
  const [disabledAiTargetAudience, setDisabledAiTargetAudience] = useState(true);
  const [disabledPassword, setDisabledPassword] = useState(true);
  const [disabledRole, setDisabledRole] = useState(true);
  // Analytics opt-out (right-to-object, CNIL exemption). Two layers, opted-out wins:
  //  - browser-local: the Umami tracker skips sending when localStorage['umami.disabled'] === '1'.
  //  - account-level: preferences.analytics_opt_out, honored server-side by the
  //    apiAnalytics middleware (also covers non-browser VR/Unity clients).
  // Not part of the profile form — the checkbox persists preferences imperatively.
  const [umamiOptOut, setUmamiOptOut] = useState(
    () =>
      localStorage.getItem("umami.disabled") === "1" ||
      (currentUser as any)?.preferences?.analytics_opt_out === true,
  );
  // Baseline for the "at least one change" save gate. The checkbox lives outside
  // react-hook-form, so isDirty never reflects it — we compare against the value
  // captured at mount. Reset after a successful save so the button re-disables.
  const initialUmamiOptOut = useRef(umamiOptOut);

  // Sync-down: a user who opted out on machine A (account-level) is dark on machine
  // B after login, without waiting for a checkbox visit. Opted-out wins, so we set
  // localStorage from the account flag but NEVER auto-clear a browser-only opt-out
  // when the account flag is false. Also refresh the baseline so the Save button
  // doesn't spuriously light up from this programmatic change.
  useEffect(() => {
    const accountOptOut = (currentUser as any)?.preferences?.analytics_opt_out === true;
    if (accountOptOut && localStorage.getItem("umami.disabled") !== "1") {
      localStorage.setItem("umami.disabled", "1");
      setUmamiOptOut(true);
      initialUmamiOptOut.current = true;
    }
  }, [currentUser]);

  const modalState = useAppSelector(getCurrentModalCurrentState);
  // const userPrefLanguage = currentUser?.preferred_language as ILanguage;

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    watch,
    getValues,
    setFocus,
    formState: { isDirty, errors: errorsProfile },
  } = useForm<ProfileValues>({
    defaultValues: {
      firstname: currentUser?.firstname,
      lastname: currentUser?.lastname,
      email: currentUser?.email,
      source_avatar: Number(currentUser?.source_avatar?.id),
      avatar: currentUser?.avatar3d_url,
      preferred_language: Number((currentUser?.preferred_language as ILanguage)?.id),
      wantToReceiveEmailAboutReleases: getReleaseOptIn(currentUser),
      ai_metier: getAiProfile(currentUser).metier,
      ai_targetAudience: getAiProfile(currentUser).targetAudience,
    },
  });
  useEffect(() => {
    if (modalState.showConfirmationModal.show === false) {
      setValue("email", currentUser?.email ? currentUser?.email : "");
    }
  }, [modalState]);
  const watchEmail = watch("email");

  const onSubmitProfile = (data: ProfileValues) => {
    void dispatch(
      checkEmail({
        componentId: CHECK_EMAIL_COMPONENT_ID,
        email: data.email,
        id: currentUser?.id,
      }),
    ).then((response) => {
      if (response.payload) {
        const nextPreferences = buildNextPreferences(
          (currentUser as any)?.preferences,
          data.wantToReceiveEmailAboutReleases,
        );

        // Persist AI profile in preferences
        if (data.ai_metier?.trim()) {
          nextPreferences.aiProfile = {
            metier: data.ai_metier.trim(),
            targetAudience: data.ai_targetAudience?.trim() || undefined,
          };
        } else {
          // Clear AI profile if metier is empty
          delete nextPreferences.aiProfile;
        }

        const updatedUser = {
          id: Number(currentUser?.id),
          firstname: data.firstname,
          lastname: data.lastname,
          email: data.email?.toLocaleLowerCase(),
          preferred_language: data.preferred_language,
          source_avatar: data.source_avatar,
          preferences: nextPreferences,
        };

        setIsConfirmPasswordOpen(true);
        dispatch(closeStandardModal("modalConfirmPasswordForEmail"));

        dispatch(updateUser({ componentId: PROFILE_COMPONENT_ID, ...updatedUser }))
          .then(() => {
            reset({
              firstname: data.firstname,
              lastname: data.lastname,
              email: data.email,
              source_avatar: data.source_avatar,
              preferred_language: data.preferred_language,
              wantToReceiveEmailAboutReleases: data.wantToReceiveEmailAboutReleases,
              ai_metier: data.ai_metier,
              ai_targetAudience: data.ai_targetAudience,
            });

            setDisabledFirstName(true);
            setDisabledLastName(true);
            setDisabledEmail(true);
            setDisabledPassword(true);
            setDisabledAiMetier(true);
            setDisabledAiTargetAudience(true);
            initialUmamiOptOut.current = umamiOptOut;

            setTimeout(() => {
              dispatch(setAlert({ type: "success", msg: t("alerts.profileEditSuccess") }));
            }, 1);
          })
          .catch((e: any) => {
            console.error("catch: ", e);
          });
      } else {
        reset({
          firstname: currentUser?.firstname,
          lastname: currentUser?.lastname,
          email: currentUser?.email,
          source_avatar: Number(currentUser?.source_avatar?.id),
          preferred_language: Number((currentUser?.preferred_language as ILanguage)?.id),
          wantToReceiveEmailAboutReleases: getReleaseOptIn(currentUser),
        });
        setDisabledFirstName(true);
        setDisabledLastName(true);
        setDisabledEmail(true);
        setDisabledPassword(true);
        setDisabledAiMetier(true);
        setDisabledAiTargetAudience(true);
        dispatch(
          setAlert({
            type: "danger",
            msg: t("alerts.emailCheck"),
          }),
        );
      }
    });
  };

  const cancel = () => {
    reset({
      firstname: currentUser?.firstname,
      lastname: currentUser?.lastname,
      email: currentUser?.email,
      source_avatar: Number(currentUser?.source_avatar?.id),
      preferred_language: Number((currentUser?.preferred_language as ILanguage)?.id),
      wantToReceiveEmailAboutReleases: getReleaseOptIn(currentUser),
      ai_metier: getAiProfile(currentUser).metier,
      ai_targetAudience: getAiProfile(currentUser).targetAudience,
    });
    setDisabledFirstName(true);
    setDisabledLastName(true);
    setDisabledEmail(true);
    setDisabledPassword(true);
    setDisabledAiMetier(true);
    setDisabledAiTargetAudience(true);
  };

  const handleAvatar3dChange = (url: string) => {
    void dispatch(
      updateUser({
        componentId: PROFILE_COMPONENT_ID,
        id: Number(currentUser?.id),
        avatar3d_url: url,
      }),
    );
  };

  const userIsAdminOfOrg = Number(currentUser?.id) === Number(organization?.admin.id);
  const hasAnythingChanged =
    Number(currentUser?.source_avatar?.id) !== watch("source_avatar") ||
    isDirty ||
    umamiOptOut !== initialUmamiOptOut.current;

  const getRoleName = (currentUser?: IUser) => {
    if (userIsAdminOfOrg) {
      return t("general.roles.admin");
    } else if (Number(currentUser?.role.id) === 5) {
      return t("general.roles.creator");
    } else {
      return t("general.roles.enduser");
    }
  };

  const hasSomethingChanged = () => {
    if (currentUser?.firstname !== getValues("firstname")) {
      return true;
    }

    if (currentUser?.lastname !== getValues("lastname")) {
      return true;
    }

    if (currentUser?.email !== getValues("email")) {
      return true;
    }

    if (Number(currentUser?.source_avatar.id) !== getValues("source_avatar")) {
      return true;
    }

    if (
      Number((currentUser?.preferred_language as ILanguage).id) !==
      Number(getValues("preferred_language"))
    ) {
      return true;
    }
    if (getReleaseOptIn(currentUser) !== !!getValues("wantToReceiveEmailAboutReleases")) {
      return true;
    }
    const savedAi = getAiProfile(currentUser);
    if (savedAi.metier !== (getValues("ai_metier") || "")) {
      return true;
    }
    if (savedAi.targetAudience !== (getValues("ai_targetAudience") || "")) {
      return true;
    }
    return false;
  };

  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
    hasSomethingChanged(),
  );
  const resetTutorial = () => {
    void dispatch(userOpenedTutorialReset({ componentId: PROFILE_COMPONENT_ID })).then(
      (res: any) => {
        if (res.payload) {
          dispatch(
            setAlert({
              type: "success",
              msg: t("alerts.tutorialResetSuccess"),
            }),
          );
        } else {
          dispatch(
            setAlert({
              type: "danger",
              msg: t("alerts.tutorialResetFail"),
            }),
          );
        }
      },
    );
  };

  return (
    <>
      <ChangePasswordPopup email={currentUser?.email} />
      <Avatar3dPopup
        currentUserAvatar3dUrl={avatar3dSignedUrl}
        handleAvatar3dChange={handleAvatar3dChange}
      />
      <ModalGallery
        nodeId="profile"
        allowedMediaTypeOverride="image"
        onMediaChange={(e: any) => {
          const selectedMediaId = Number(e.id);
          setValue("source_avatar", selectedMediaId);
          dispatch(closeStandardModal("modal-gallery"));
        }}
      />
      <div className="relative grow w-full pl-4 pr-4 px-4 mx-auto personalize-scroll-visible overflow-y-auto">
        <h2 className="text-2xl font-medium mb-2 mt-2 xl:mt-4">
          {t("pages.profile.tabs.myDetails")}
        </h2>
        <h3 className="py-3">{t("pages.profile.main.subheading")}</h3>
        <div className="completely-centeredProfile">
          <IsLoading componentId={PROFILE_COMPONENT_ID} showSuccess={false} />
        </div>
        <div className="mt-2 border-gray-300">
          <form className="h-full" onSubmit={handleSubmit(onSubmitProfile)}>
            <ProfileInputStyle
              title={t("pages.profile.main.firstName")}
              disabled={disabledFirstName}
              setDisabled={setDisabledFirstName}
              setFocus={() => setFocusOnInput("firstname")}
            >
              <Input
                type="text"
                highlight={disabledFirstName}
                readOnly={disabledFirstName}
                registerFct={() => register("firstname")}
                className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 max-w-max text-center sm:text-left border-0 ${ ? }"
                placeholder={t("pages.profile.main.firstNamePlaceholder")}
              />
            </ProfileInputStyle>
            <ProfileInputStyle
              title={t("pages.profile.main.lastName")}
              disabled={disabledLastName}
              setDisabled={setDisabledLastName}
              setFocus={() => setFocusOnInput("lastname")}
            >
              <Input
                type="text"
                highlight={disabledLastName}
                readOnly={disabledLastName}
                registerFct={() => register("lastname")}
                className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600  max-w-max text-center sm:text-left border-0 ${ ? }"
                placeholder={t("pages.profile.main.lastNamePlaceholder")}
              />
            </ProfileInputStyle>
            <ProfileInputStyle
              title={t("pages.profile.main.email")}
              disabled={disabledEmail}
              setDisabled={setDisabledEmail}
            >
              <Input
                type="email"
                readOnly={disabledEmail}
                registerFct={() => register("email", { required: true, pattern: /^\S+@\S+$/i })}
                className="mr-2 font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 text-center max-w-max sm:text-left border-0 ${ ? }"
                placeholder={t("pages.profile.main.emailPlaceholder")}
              />
              <ChangeEmailPoppup
                setIsConfirmPasswordOpen={setIsConfirmPasswordOpen}
                register={register}
                onsubmit={handleSubmit(onSubmitProfile)}
                isConfirmPasswordOpen={isConfirmPasswordOpen}
                email={watchEmail}
              />
              <ErrorMessage
                errors={errorsProfile}
                name="email"
                render={() => (
                  <div className="font-small text-red-700">{"Ne peux pas être vide!"}</div>
                )}
              />
            </ProfileInputStyle>
            <ProfileInputStyle title={t("pages.profile.main.photo")} setValueFct={setValue}>
              <RoundImagePreview
                componentId={PROFILE_COMPONENT_ID}
                mediaId_OrIsItAnObjectLol={watch("source_avatar")}
                alt="Logo"
                bigger
              />
            </ProfileInputStyle>
            <ProfileInputStyle
              title={t("general.avatar")}
              avatarStatus={currentUser?.avatar3d_url ? "ready" : "loading"}
            >
              {avatarThumbnailUrl ? (
                <img
                  className="inline object-cover w-16 h-16 mr-10 sm:ml-4 mr-2 rounded-full border border-gray-300"
                  src={avatarThumbnailUrl}
                  alt="Avatar"
                />
              ) : avatar3dSignedUrl ? (
                // Render the avatar directly from its url (gallery match preferred, else the
                // user's avatar3d_url). Don't gate on the gallery: an avatar living on another
                // bucket (e.g. the default avatar) is never in state.media.all, which used to
                // leave this row stuck on a dash or a forever-spinner. AvatarPreview3d handles
                // its own loading spinner and degrades to a dash if the model fails to load.
                <AvatarPreview3d
                  url={avatar3dSignedUrl}
                  className="w-16 h-16 mr-10 sm:ml-4 mr-2 rounded-full border border-gray-300 overflow-hidden"
                  canvasClassName="w-[130%] h-[130%] -ml-[24%] -mt-[21%]"
                />
              ) : !currentUser ? (
                <Spinner relative />
              ) : (
                <p className="text-sm text-gray-400 italic ml-4">
                  {t("pages.profile.main.noAvatarCta")}
                </p>
              )}
            </ProfileInputStyle>

            <div className="w-full flex flex-col sm:flex-row items-center py-2 px-2 xl:py-3 xl:px-4 border-t border-gray-300">
              <p className="w-full sm:w-1/3 text-sm text-center sm:text-left">
                {t("pages.profile.main.language")}
              </p>

              <Dropdown
                options={languageDropdownOptions}
                asDefaultValue
                languague
                defaultValueIndex={languageDropdownOptions.findIndex((v) => {
                  const userPrefLanguage = currentUser?.preferred_language as ILanguage;
                  return Number(userPrefLanguage?.id) === Number(v.value);
                })}
                registerFct={() => register("preferred_language")}
              />
            </div>

            {!isEnduser && (
              <div className="w-full flex flex-col sm:flex-row items-center py-2 px-2 xl:py-3 xl:px-4 border-t border-gray-300">
                <div className="w-full sm:w-1/3 flex items-center justify-center sm:justify-start text-sm text-center sm:text-left">
                  <p>{t("pages.profile.main.wantToReceiveEmailAboutReleases")}</p>
                  <Tooltip message={t("tooltip.wantToReceiveEmailAboutReleases")}>
                    <span className="ml-2 inline-block cursor-help select-none">ⓘ</span>
                  </Tooltip>
                </div>

                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-indigo-600 shadow-sm focus:ring-indigo-500"
                  {...register("wantToReceiveEmailAboutReleases")}
                />
              </div>
            )}

            <div className="w-full flex flex-col sm:flex-row items-center py-2 px-2 xl:py-3 xl:px-4 border-t border-gray-300">
              <div className="w-full sm:w-1/3 flex items-center justify-center sm:justify-start text-sm text-center sm:text-left">
                <p>{t("pages.profile.main.umamiOptOut")}</p>
                <Tooltip
                  message={t("tooltip.umamiOptOut")}
                  forceSingleLine={false}
                  lineLength="w-72"
                >
                  <span className="ml-2 inline-block cursor-help select-none">ⓘ</span>
                </Tooltip>
              </div>

              <input
                type="checkbox"
                className="rounded border-gray-300 text-indigo-600 shadow-sm focus:ring-indigo-500"
                checked={umamiOptOut}
                onChange={(e) => {
                  const optedOut = e.target.checked;
                  setUmamiOptOut(optedOut);
                  // Browser-local tracker honors this immediately (verbatim as before).
                  if (optedOut) {
                    localStorage.setItem("umami.disabled", "1");
                  } else {
                    localStorage.removeItem("umami.disabled");
                  }
                  // Account-level opt-out: spread the current preferences bag and
                  // override just analytics_opt_out — same imperative preferences PUT
                  // the AI-profile save uses (onSubmitProfile). Fire-and-forget; the
                  // checkbox UI is never blocked on the request.
                  const nextPreferences = buildNextPreferences(
                    (currentUser as any)?.preferences,
                    getReleaseOptIn(currentUser),
                  );
                  nextPreferences.analytics_opt_out = optedOut;
                  dispatch(
                    updateUser({
                      componentId: PROFILE_COMPONENT_ID,
                      id: Number(currentUser?.id),
                      preferences: nextPreferences,
                    }),
                  ).catch((err: any) => {
                    console.error("catch: ", err);
                  });
                }}
              />
            </div>

            {!isEnduser && (
              <>
                <h3 className="text-lg font-medium mt-6 mb-1">
                  {t("pages.profile.main.aiProfileTitle")}
                </h3>
                <p className="text-sm text-gray-500 mb-2">
                  {t("pages.profile.main.aiProfileDescription")}
                </p>

                <ProfileInputStyle
                  title={t("pages.profile.main.aiMetier")}
                  disabled={disabledAiMetier}
                  setDisabled={setDisabledAiMetier}
                >
                  <Input
                    type="text"
                    highlight={disabledAiMetier}
                    readOnly={disabledAiMetier}
                    registerFct={() => register("ai_metier")}
                    className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 flex-1 text-center sm:text-left border-0"
                    placeholder={t("pages.profile.main.aiMetierPlaceholder")}
                  />
                </ProfileInputStyle>

                <ProfileInputStyle
                  title={t("pages.profile.main.aiTargetAudience")}
                  disabled={disabledAiTargetAudience}
                  setDisabled={setDisabledAiTargetAudience}
                >
                  <Input
                    type="text"
                    highlight={disabledAiTargetAudience}
                    readOnly={disabledAiTargetAudience}
                    registerFct={() => register("ai_targetAudience")}
                    className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 flex-1 text-center sm:text-left border-0"
                    placeholder={t("pages.profile.main.aiTargetAudiencePlaceholder")}
                  />
                </ProfileInputStyle>
              </>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                className="btn-alternative-outline mr-4"
                onClick={() => {
                  cancel();
                }}
              >
                {t("general.cancel")}
              </button>
              <button className="btn-primary-fill" type="submit" disabled={!hasAnythingChanged}>
                {t("general.save")}
              </button>
            </div>
          </form>

          <h2 className="text-2xl font-medium mb-3 mt-8">{t("pages.profile.tabs.myAccount")}</h2>
          <ProfileInputStyle
            title={t("general.password")}
            disabled={disabledPassword}
            setDisabled={setDisabledPassword}
          >
            <Input
              type="text"
              readOnly={disabledPassword}
              className=" mr-2 font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 read-only:placeholder-gray-600  max-w-max text-center sm:text-left border-0 ${ ? }"
              placeholder={"*********"}
            />
          </ProfileInputStyle>

          <ProfileInputStyle
            title={t("general.roles.role")}
            disabled={disabledRole}
            setDisabled={setDisabledRole}
          >
            <Input
              type="text"
              readOnly={disabledRole}
              className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 max-w-max text-center sm:text-left border-0 ${ ? }"
              value={getRoleName(currentUser)}
            />
          </ProfileInputStyle>
          <div className="flex justify-end mb-4 mr-1">
            <button
              className="btn-alternative-outline hidden" // WIXAR-4051 🙈
              onClick={() => resetTutorial()}
              disabled={buttonResetDisabled}
            >
              {t("pages.profile.tabs.resetTutorial")}
            </button>
          </div>
        </div>
      </div>
      <ModalConfirmBeforeNavigatingOut
        showDialog={showPrompt}
        cancelNavigation={cancelNavigation}
        confirmNavigation={confirmNavigation}
      />
    </>
  );
};

export default ProfileMain;
